/**
 * This internal class is for providing consistent errors and formatting for networking errors
 * for Sentry. All error classes in this file should use this for its useful data.
 */
class NetworkingError extends Error {
  constructor(name, message) {
    super(message)
    this.timestamp = new Date()

    Object.defineProperty(this, 'message', {
      configurable: true,
      enumerable: false,
      value: message,
      writable: true,
    })

    Object.defineProperty(this, 'name', {
      configurable: true,
      enumerable: false,
      value: name,
      writable: true,
    })
  }
}

/**
 * A bunch of different error type names we may want to check for elsewhere. Should be used below
 * @type {Object}
 */
export const ErrorName = {
  cancellation: 'CancellationError',
  serverResponse: 'ServerResponseError',
  coded: 'CodedError',
  emptyResponse: 'ServerEmptyResponseError',
  development: 'DevelopmentError',
}

/**
 * These sorts of errors should be filtered out when being sent to Sentry since we don't need
 * to keep track of them.
 * @type {Array}
 */
export const filteredErrorTypes = [ErrorName.cancellation]

/**
 * This class encapsulates errors that were due to cancellation and formats them
 * in a standard way for Sentry.
 */
export class CancellationError extends NetworkingError {
  constructor(cancelledRequest) {
    const message = cancelledRequest.message || 'The request was cancelled'
    super(ErrorName.cancellation, message)
    this.request = cancelledRequest
  }
}

/**
 * Encapsulates errors from the server where we had a response outside of the
 * 2xx status range for better categorization for Sentry.
 */
export class ServerResponseError extends NetworkingError {
  constructor(request, { data = [], status = 0, headers = {} }) {
    const name = `${ErrorName.serverResponse} (${status}) [URL: ${request.responseURL}]`
    // We always just take the first error - note that we may be suppressing some errors to the user this way, but it keeps it cleaner
    const message = data?.errors?.[0]?.message ?? `Response resulted in a ${status} status code`
    super(name, message)
    this.status = status
    this.headers = headers
    this.request = request
    if (data.errors && data.errors.length > 1) {
      this.errors = data.errors
    } else {
      this.errorType = data.error_type || 'ServerError'
    }
  }
}

/**
 * This class encapsulates errors that have an error code and
 * formats them in a standard way for Sentry.
 */
export class CodedError extends NetworkingError {
  constructor(error) {
    const name = `${ErrorName.coded} (${error.code}: ${error.name} [URL: ${error.request.responseURL}])`
    const message = error.message || 'Error communicating with the server, please try again.'
    super(name, message)
    this.code = error.code
    this.request = error.request
  }
}

/**
 * Encapsulates errors from the server where we had no response for better
 * categorization for Sentry.
 */
export class ServerEmptyResponseError extends NetworkingError {
  constructor({ request, message }) {
    const name = `${ErrorName.emptyResponse} ${message ? `- ${message}` : ''}`
    const errorMessage = message || 'Received an empty response from the server'
    super(name, errorMessage)
    this.status = request.status || 'unknown'
    this.readyState = request.readyState || -1
    this.request = request
  }
}

/**
 * This class encapsulates errors that were due to user error in setting up a network call.
 */
export class DevelopmentError extends NetworkingError {
  constructor(message = 'Developer error') {
    super(ErrorName.development, message)
  }
}
